;(function() {
  'use strict'

  Controller.$inject = ["$element", "$scope", "$animate", "$filter", "glToast", "glUtils", "quotaCellsService", "quotaCellStateService", "quotaTextService", "QuotaPlan"];
  angular.module('app.core').component('quotaGrid', {
    controller: Controller,
    templateUrl: 'quota-grid.html',
    bindings: {
      quota: '<',
      plan: '<',
      questions: '<',
      tagCounts: '<',
      responseCount: '<',
      targetsRevision: '<',
      onChange: '&',
      onPasteChange: '&',
    },
  })

  /* @ngInject */
  function Controller(
    $element,
    $scope,
    $animate,
    $filter,
    glToast,
    glUtils,
    quotaCellsService,
    quotaCellStateService,
    quotaTextService,
    QuotaPlan
  ) {
    var ctrl = this

    var numberFilter = $filter('number')
    var cellsByKey = {}
    var positionByKey = {}
    var signature = null
    var keyboardInteraction = false
    var focusing = false
    var SCROLL_MARGIN = 8
    var pasteSnapshot = null
    var cloneUp = false
    var cloneTimer = null
    var columnEnds = []
    var pinnedRightWidth = 0
    var legendMoreNode = null
    var scrollBox = null
    var cloneBox = null

    ctrl.$onInit = onInit
    ctrl.$postLink = postLink
    ctrl.$onDestroy = onDestroy
    ctrl.$onChanges = onChanges
    ctrl.$doCheck = doCheck

    ctrl.onCellChange = onCellChange
    ctrl.getTotalState = getTotalState
    ctrl.getTotalTarget = getTotalTarget
    ctrl.getTotalFillPercent = getTotalFillPercent
    ctrl.getTotalStatParts = getTotalStatParts
    ctrl.getTotalHint = getTotalHint

    ctrl.focusCell = focusCell
    ctrl.onCellFocusRequest = onCellFocusRequest
    ctrl.onGridKeyDown = onGridKeyDown
    ctrl.onGridMouseDown = onGridMouseDown
    ctrl.onGridPaste = onGridPaste

    ctrl.focusedCellKey = null

    function onInit() {
      $animate.enabled($element, false)
    }

    function postLink() {
      $element[0].addEventListener('scroll', onDescendantScroll, true)
      angular.element(window).on('resize', onWindowResize)
      $element[0].addEventListener('focusout', onGridFocusOut)

      scheduleCloneLayout()
    }

    function onDestroy() {
      $element[0].removeEventListener('scroll', onDescendantScroll, true)
      $element[0].removeEventListener('focusout', onGridFocusOut)
      angular.element(window).off('resize', onWindowResize)

      scheduleCloneSettle.cancel()

      if (cloneTimer) {
        clearTimeout(cloneTimer)
        cloneTimer = null
      }

      discardPasteSnapshot()
    }

    function onChanges(changes) {
      if (
        changes.quota ||
        changes.plan ||
        changes.questions ||
        changes.tagCounts
      ) {
        build()
      }
    }

    function doCheck() {
      checkCloneVisibility()

      var next = buildSignature()
      if (next === signature) return

      signature = next
      build()
    }

    // every binding is one-way, so adding a variable never changes the quota reference - this signature is what catches it
    function buildSignature() {
      if (!ctrl.quota) return ''

      return [
        ctrl.quota.id,
        ctrl.quota.mode,
        _.map(ctrl.quota.variables, 'id').join(','),
        (ctrl.questions || []).length,
        ctrl.targetsRevision || 0,
      ].join('|')
    }

    function build() {
      scheduleCloneLayout()
      discardPasteSnapshot()

      cellsByKey = {}
      positionByKey = {}
      ctrl.rows = []
      ctrl.columns = []
      ctrl.rowVariableLabels = []
      ctrl.columnsLabel = null
      ctrl.hasTotalColumn = false
      ctrl.pinnedColumnIndex = -1
      ctrl.isBroken = false

      if (!ctrl.quota || !ctrl.plan) return

      var axes = quotaCellsService.getAxes(ctrl.quota, ctrl.questions)

      ctrl.variableCount = axes.length
      if (!axes.length) return

      ctrl.isBroken = quotaCellsService.hasEmptyAxis(axes)
      if (ctrl.isBroken) return

      if (ctrl.quota.isIndependent()) {
        buildSeparate(axes)
      } else {
        buildInterlocked(axes)
      }

      indexPositions()
      refreshTotals()
    }

    function indexPositions() {
      ctrl.rows.forEach(function(row, rowIndex) {
        if (row.type !== 'DATA') return

        row.cells.forEach(function(cell, columnIndex) {
          positionByKey[cell.cellKey] = { row: rowIndex, col: columnIndex }
        })
      })
    }

    function buildInterlocked(axes) {
      var columnAxis = axes.length > 1 ? _.last(axes) : null
      var rowAxes = axes.length > 1 ? axes.slice(0, -1) : axes

      ctrl.rowVariableLabels = _.map(rowAxes, 'label')
      ctrl.columnsLabel = columnAxis ? columnAxis.label : null
      ctrl.hasTotalColumn = !!columnAxis
      ctrl.pinnedColumnIndex = rowAxes.length - 1
      ctrl.columns = columnAxis ? columnAxis.options : [{ id: null, label: '' }]

      var spans = rowAxes.map(function(axis, index) {
        return _.reduce(
          rowAxes.slice(index + 1),
          function(product, inner) {
            return product * inner.options.length
          },
          1
        )
      })

      var combinations = cartesian(rowAxes)
      var groupIndex = 0

      combinations.forEach(function(combination, rowIndex) {
        var headers = rowAxes.map(function(axis, index) {
          return {
            label: combination[index].label,
            span: spans[index],
            index: index,
            isFirst: rowIndex % spans[index] === 0,
          }
        })

        var cells = ctrl.columns.map(function(column) {
          var optionIdByVariableId = {}

          rowAxes.forEach(function(axis, index) {
            optionIdByVariableId[axis.variable.id] = combination[index].id
          })

          if (columnAxis) {
            optionIdByVariableId[columnAxis.variable.id] = column.id
          }

          return makeCell(optionIdByVariableId)
        })

        var startsOuterGroup =
          rowIndex > 0 &&
          _.some(headers.slice(0, -1), function(header) {
            return header.isFirst
          })

        if (startsOuterGroup) groupIndex++

        ctrl.rows.push({
          type: 'DATA',
          headers: headers,
          cells: cells,
          isGroupStart: startsOuterGroup,
          isGroupAlt: groupIndex % 2 === 1,
        })
      })

      ctrl.rows.push({
        type: 'TOTAL',
        labels: rowAxes.map(function(axis, index) {
          return index === ctrl.pinnedColumnIndex ? 'Total' : ''
        }),
        cells: [],
      })
    }

    function buildSeparate(axes) {
      ctrl.columns = [{ id: null, label: '' }]
      ctrl.hasTotalColumn = false
      ctrl.rowVariableLabels = []

      ctrl.pinnedColumnIndex = 0

      var hasQuestionAxis = _.some(axes, function(axis) {
        return axis.variable.isQuestion()
      })

      axes.forEach(function(axis) {
        ctrl.rows.push({
          type: 'HEADING',
          label: axis.label,
          countingBadge:
            axis.variable.isTag() && hasQuestionAxis
              ? 'includes past responses'
              : null,
        })

        axis.options.forEach(function(option) {
          var optionIdByVariableId = {}
          optionIdByVariableId[axis.variable.id] = option.id

          ctrl.rows.push({
            type: 'DATA',
            headers: [
              { label: option.label, span: 1, isFirst: true, index: 0 },
            ],
            cells: [makeCell(optionIdByVariableId)],
          })
        })
      })
    }

    function makeCell(optionIdByVariableId) {
      var cellKey = ctrl.quota.getCellKey(optionIdByVariableId)
      var counterName = ctrl.quota.getCounterName(optionIdByVariableId)
      var counts = ctrl.tagCounts || {}

      var cell = {
        cellKey: cellKey,
        target: ctrl.plan.getTarget(ctrl.quota.id, cellKey),
        // a missing count and a zero count mean the same thing, which is not true of the target on the line above
        achieved: counts[counterName] || 0,
      }

      // two cells on one key share a target, a counter and the focus, and nothing else reports it - see toToken in QuotaDefinition
      if (cellsByKey[cellKey]) {
        console.error(
          'quotaGrid: duplicate cell key ' +
            cellKey +
            ' in quota ' +
            ctrl.quota.id +
            ' - option ids ' +
            _.values(optionIdByVariableId).join(', ')
        )
      }

      cellsByKey[cellKey] = cell

      return cell
    }

    function cartesian(axes) {
      var result = [[]]

      axes.forEach(function(axis) {
        var next = []

        result.forEach(function(prefix) {
          axis.options.forEach(function(option) {
            next.push(prefix.concat([option]))
          })
        })

        result = next
      })

      return result
    }

    function refreshTotals() {
      var dataRows = _.filter(ctrl.rows, { type: 'DATA' })

      if (ctrl.hasTotalColumn) {
        dataRows.forEach(function(row) {
          row.total = summarise(row.cells)
        })
      }

      var totalRow = _.find(ctrl.rows, { type: 'TOTAL' })
      if (!totalRow) return

      totalRow.cells = ctrl.columns.map(function(column, columnIndex) {
        return summarise(
          dataRows.map(function(row) {
            return row.cells[columnIndex]
          })
        )
      })

      totalRow.total = summarise(
        _.flatMap(dataRows, function(row) {
          return row.cells
        })
      )
    }

    function summarise(cells) {
      var total = 0
      var achieved = 0
      var blankCount = 0
      var setCount = 0

      cells.forEach(function(cell) {
        achieved += cell.achieved

        if (_.isNumber(cell.target)) {
          total += cell.target
          setCount++
        } else {
          blankCount++
        }
      })

      return {
        total: setCount ? total : null,
        hasBlank: blankCount > 0,
        blankCount: blankCount,
        achieved: achieved,
      }
    }

    function getTotalState(cell) {
      return quotaCellStateService.getState(
        cell.hasBlank ? null : cell.total,
        cell.achieved
      )
    }

    function getTotalTarget(cell) {
      if (cell.total === null) return 'No limit'
      return numberFilter(cell.total) + (cell.hasBlank ? '+' : '')
    }

    function getTotalFillPercent(cell) {
      return quotaCellStateService.getFillPercent(cell.total, cell.achieved)
    }

    function getTotalStatParts(cell) {
      return quotaTextService.formatProgress(
        cell.achieved,
        cell.total,
        cell.hasBlank
      )
    }

    function getTotalHint(cell) {
      if (cell.total === null) return 'No limit set'

      if (cell.hasBlank) {
        return (
          cell.total +
          ' set, plus ' +
          cell.blankCount +
          (cell.blankCount === 1 ? ' cell' : ' cells') +
          ' with no limit'
        )
      }

      return (
        numberFilter(
          quotaCellStateService.getRemaining(cell.total, cell.achieved)
        ) + ' remaining'
      )
    }

    function onCellChange(cellKey, value) {
      discardPasteSnapshot()
      writeTarget(cellKey, value)
      refreshTotals()
      ctrl.onChange()
    }

    function writeTarget(cellKey, value) {
      ctrl.plan.setTarget(ctrl.quota.id, cellKey, value)

      var cell = cellsByKey[cellKey]
      if (cell) cell.target = value
    }

    function focusCell(row, col) {
      // focusing the input fires the focus event that calls back into here, so this guard is what stops the recursion
      if (focusing) return false

      var cell = getDataCell(row, col)
      if (!cell) return false

      focusing = true

      try {
        ctrl.focusedCellKey = cell.cellKey

        var td = findCellElement(row, col)
        if (!td) return false

        var input = td.querySelector('input')
        if (!input) return false

        if (document.activeElement !== input) {
          input.focus({ preventScroll: true })
        }

        if (keyboardInteraction) {
          input.select()
          scrollCellIntoView(td)
        }

        return true
      } finally {
        focusing = false
      }
    }

    function onCellFocusRequest(cellKey) {
      var position = positionByKey[cellKey]
      if (!position) return

      focusCell(position.row, position.col)
    }

    function onGridFocusOut(event) {
      if (ctrl.focusedCellKey === null) return

      var receiving = event.relatedTarget
      if (receiving && $element[0].contains(receiving)) return

      ctrl.focusedCellKey = null
      requestDigest()
    }

    function requestDigest() {
      $scope.$evalAsync(angular.noop)
    }

    function getDataCell(row, col) {
      var descriptor = ctrl.rows && ctrl.rows[row]
      if (!descriptor || descriptor.type !== 'DATA') return null

      return descriptor.cells[col] || null
    }

    function findCellElement(row, col) {
      return $element[0].querySelector(
        'tr[data-quota-row="' + row + '"] td[data-quota-col="' + col + '"]'
      )
    }

    var Keys = {
      ENTER: 'ENTER',
      LEFT: 'LEFT',
      UP: 'UP',
      RIGHT: 'RIGHT',
      DOWN: 'DOWN',
    }

    var KEY_BY_CODE = {
      13: Keys.ENTER,
      37: Keys.LEFT,
      38: Keys.UP,
      39: Keys.RIGHT,
      40: Keys.DOWN,
    }

    var KEY_BY_NAME = {
      Enter: Keys.ENTER,
      ArrowLeft: Keys.LEFT,
      ArrowUp: Keys.UP,
      ArrowRight: Keys.RIGHT,
      ArrowDown: Keys.DOWN,
      Left: Keys.LEFT,
      Up: Keys.UP,
      Right: Keys.RIGHT,
      Down: Keys.DOWN,
    }

    function getNavigationKey(event) {
      return KEY_BY_CODE[event.keyCode] || KEY_BY_NAME[event.key] || null
    }

    var KEY_CODE_Z = 90

    function isUndoKey(event) {
      if (!event.ctrlKey && !event.metaKey) return false
      if (event.shiftKey) return false

      return (
        event.keyCode === KEY_CODE_Z || event.key === 'z' || event.key === 'Z'
      )
    }

    function onGridKeyDown(event) {
      keyboardInteraction = true

      if (isUndoKey(event)) {
        if (!pasteSnapshot) return

        event.preventDefault()
        undoPaste()
        return
      }

      var position = positionByKey[ctrl.focusedCellKey]
      if (!position) return

      switch (getNavigationKey(event)) {
        case Keys.UP:
          return moveByRow(event, position, -1)
        case Keys.DOWN:
          return moveByRow(event, position, 1)
        case Keys.ENTER:
          return moveByRow(event, position, event.shiftKey ? -1 : 1)
        case Keys.LEFT:
          return moveByColumn(event, position, -1)
        case Keys.RIGHT:
          return moveByColumn(event, position, 1)
      }
    }

    function onGridMouseDown() {
      keyboardInteraction = false
    }

    function moveByRow(event, position, delta) {
      event.preventDefault()

      var row = findDataRow(position.row + delta, delta)
      if (row === -1) return

      focusCell(row, position.col)
    }

    function moveByColumn(event, position, delta) {
      if (!isCaretAtEdge(event.target, delta)) return

      var col = position.col + delta
      if (!getDataCell(position.row, col)) return

      event.preventDefault()
      focusCell(position.row, col)
    }

    function isCaretAtEdge(node, delta) {
      if (!node || node.tagName !== 'INPUT') return true

      var start
      var end

      try {
        start = node.selectionStart
        end = node.selectionEnd
      } catch (e) {
        return true
      }

      if (!_.isNumber(start) || !_.isNumber(end)) return true
      if (start !== end) return false

      return delta < 0 ? start === 0 : start === (node.value || '').length
    }

    function findDataRow(start, delta) {
      for (var i = start; i >= 0 && i < ctrl.rows.length; i += delta) {
        if (ctrl.rows[i].type === 'DATA') return i
      }

      return -1
    }

    var FIELD_SEPARATOR = '\t'
    var LINE_SEPARATOR = /\r\n|\r|\n/
    var NUMERIC = /^(\d+|\d{1,3}(,\d{3})+)$/
    var GROUPING = /,/g

    function onGridPaste(event) {
      var anchor = positionByKey[ctrl.focusedCellKey]
      if (!anchor) return

      var text = glUtils.getClipboardData(event)
      if (!text) return

      var block = stripHeaders(parseBlock(text))
      if (!block.length) return

      applyBlock(anchor, block)
    }

    function parseBlock(text) {
      var lines = String(text).split(LINE_SEPARATOR)

      if (lines.length > 1 && _.last(lines) === '') {
        lines.pop()
      }

      return lines.map(function(line) {
        return line.split(FIELD_SEPARATOR).map(function(field) {
          return field.trim()
        })
      })
    }

    function stripHeaders(rows) {
      return stripHeaderColumn(stripHeaderRow(rows))
    }

    function stripHeaderRow(rows) {
      if (rows.length < 2) return rows

      return isHeaderLine(rows[0]) ? rows.slice(1) : rows
    }

    function stripHeaderColumn(rows) {
      var hasBody = _.some(rows, function(fields) {
        return fields.length > 1
      })

      if (!hasBody) return rows

      var firstColumn = rows.map(function(fields) {
        return fields[0]
      })

      if (!isHeaderLine(firstColumn)) return rows

      return rows.map(function(fields) {
        return fields.slice(1)
      })
    }

    function isHeaderLine(fields) {
      var hasContent = _.some(fields, function(field) {
        return field !== ''
      })

      if (!hasContent) return false

      return !_.some(fields, function(field) {
        return NUMERIC.test(field)
      })
    }

    function applyBlock(anchor, block) {
      var report = { pasted: 0, cleared: 0, ignored: 0, clipped: 0 }
      var snapshot = {}
      var rowIndex = anchor.row

      block.forEach(function(fields) {
        if (rowIndex === -1) {
          report.clipped += fields.length
          return
        }

        fields.forEach(function(field, offset) {
          var cell = getDataCell(rowIndex, anchor.col + offset)
          if (!cell) {
            report.clipped++
            return
          }

          captureAndWrite(cell, field, snapshot, report)
        })

        rowIndex = findDataRow(rowIndex + 1, 1)
      })

      finishPaste(snapshot, report)
    }

    function captureAndWrite(cell, field, snapshot, report) {
      var value

      if (field === '') {
        value = null
      } else if (NUMERIC.test(field)) {
        value = parseInt(field.replace(GROUPING, ''), 10)
        if (value > QuotaPlan.MAX_TARGET) {
          report.ignored++
          return
        }
      } else {
        report.ignored++
        return
      }

      if (!Object.prototype.hasOwnProperty.call(snapshot, cell.cellKey)) {
        snapshot[cell.cellKey] = ctrl.plan.getTarget(
          ctrl.quota.id,
          cell.cellKey
        )
      }

      writeTarget(cell.cellKey, value)

      if (value === null) {
        report.cleared++
      } else {
        report.pasted++
      }
    }

    function finishPaste(snapshot, report) {
      var touched = _.keys(snapshot).length
      var summary = describePaste(report)

      if (!touched) {
        glToast.show(summary)
        return
      }

      refreshTotals()
      ctrl.onChange()
      setPasteSnapshot(snapshot, summary)
      glToast.show(summary)
    }

    function describePaste(report) {
      var parts = [
        'Pasted ' +
          quotaTextService.pluralise(report.pasted, 'target', 'targets'),
      ]

      if (report.cleared) {
        parts.push('cleared ' + report.cleared)
      }

      if (report.ignored) {
        parts.push(
          quotaTextService.pluralise(report.ignored, 'value', 'values') +
            ' ignored (not a number, or over ' +
            numberFilter(QuotaPlan.MAX_TARGET) +
            ')'
        )
      }

      if (report.clipped) {
        parts.push(
          quotaTextService.pluralise(report.clipped, 'value', 'values') +
            " didn't fit"
        )
      }

      return parts.join(' · ')
    }

    function undoPaste() {
      if (!pasteSnapshot) return

      var targets = pasteSnapshot.targets
      _.forEach(targets, function(previousValue, cellKey) {
        writeTarget(cellKey, previousValue)
      })

      discardPasteSnapshot()
      refreshTotals()
      ctrl.onChange()
    }

    function setPasteSnapshot(targets, summary) {
      pasteSnapshot = { targets: targets, summary: summary }

      ctrl.onPasteChange({
        $paste: { summary: summary, undo: undoPaste },
      })
    }

    function discardPasteSnapshot() {
      if (!pasteSnapshot) return

      pasteSnapshot = null

      ctrl.onPasteChange({ $paste: null })
    }

    function scrollCellIntoView(td) {
      scrollIntoViewHorizontally(td)
      scrollIntoViewVertically(td)
    }

    function scrollIntoViewHorizontally(td) {
      var box = $element[0].querySelector('.quota-grid__scroll')
      if (!box) return

      if (!angular.element(box).hasClass('-scroll-x')) return

      var boxRect = box.getBoundingClientRect()
      var cellRect = td.getBoundingClientRect()

      var visibleLeft = boxRect.left + measurePinnedWidth('.-pin-left')
      var visibleRight = boxRect.right - measurePinnedWidth('.-pin-right')

      if (cellRect.left < visibleLeft) {
        box.scrollLeft -= visibleLeft - cellRect.left + SCROLL_MARGIN
      } else if (cellRect.right > visibleRight) {
        box.scrollLeft += cellRect.right - visibleRight + SCROLL_MARGIN
      }
    }

    function measurePinnedWidth(selector) {
      var node = $element[0].querySelector(
        '.quota-grid__scroll .quota-grid__table ' + selector
      )

      return node ? node.offsetWidth : 0
    }

    function scrollIntoViewVertically(td) {
      var cellRect = td.getBoundingClientRect()
      var top = getObstructedTop()
      var bottom = window.innerHeight

      if (cellRect.top < top + SCROLL_MARGIN) {
        window.scrollBy(0, cellRect.top - top - SCROLL_MARGIN)
      } else if (cellRect.bottom > bottom - SCROLL_MARGIN) {
        window.scrollBy(0, cellRect.bottom - bottom + SCROLL_MARGIN)
      }
    }

    function getObstructedTop() {
      var top = 0

      var appBar = document.querySelector('.survey__header')
      if (appBar) {
        top = Math.max(top, appBar.getBoundingClientRect().bottom)
      }

      var card = $element[0].closest('.quota-card')
      var cardHeader = card ? card.querySelector('.quota-card__header') : null

      var floating = cardHeader ? [cardHeader] : []

      var headerCells = $element[0].querySelectorAll(
        '.quota-grid__table thead th'
      )

      for (var i = 0; i < headerCells.length; i++) {
        floating.push(headerCells[i])
      }

      floating.forEach(function(node) {
        var rect = node.getBoundingClientRect()

        if (rect.top <= top + 1 && rect.bottom > top) {
          top = rect.bottom
        }
      })

      return top
    }

    function getScrollBox() {
      if (!scrollBox || !$element[0].contains(scrollBox)) {
        scrollBox = $element[0].querySelector('.quota-grid__scroll')
      }

      return scrollBox
    }

    function getCloneBox() {
      if (!cloneBox || !$element[0].contains(cloneBox)) {
        cloneBox = $element[0].querySelector('.quota-grid__clone')
      }

      return cloneBox
    }

    function onDescendantScroll(event) {
      var box = getScrollBox()
      if (!box || event.target !== box) return

      syncClonePosition(box, getCloneBox())
    }

    function onWindowResize() {
      scheduleCloneLayout()
      scheduleCloneSettle()
    }

    function checkCloneVisibility() {
      var box = getScrollBox()
      var wanted = !!box && box.classList.contains('-scroll-x')

      if (wanted !== cloneUp) scheduleCloneLayout()
    }

    function scheduleCloneLayout() {
      if (cloneTimer) return

      cloneTimer = setTimeout(function() {
        cloneTimer = null
        syncCloneLayout()
      })
    }

    var scheduleCloneSettle = _.debounce(scheduleCloneLayout, 150)

    function syncCloneLayout() {
      var root = $element[0].querySelector('.quota-grid')
      if (!root) return

      var box = getScrollBox()
      var clone = getCloneBox()
      // the sticky header is surrendered while the grid scrolls sideways, which is the only reason this clone exists
      var wanted = !!box && !!clone && box.classList.contains('-scroll-x')

      cloneUp = wanted

      if (!wanted) {
        if (box) box.style.marginTop = ''

        updateColumnCue(null)

        root.classList.remove('-cloned-header')
        return
      }

      root.classList.add('-cloned-header')

      measureClone(box, clone)
      syncClonePosition(box, clone)
    }

    function measureClone(box, clone) {
      var realTable = box.querySelector('.quota-grid__table')
      var cloneTable = clone.querySelector('.quota-grid__table')

      if (!realTable || !cloneTable || !realTable.tHead || !cloneTable.tHead) {
        return
      }

      cloneTable.style.width = realTable.getBoundingClientRect().width + 'px'

      var realCells = lastHeaderRowCells(realTable)
      var cloneCells = lastHeaderRowCells(cloneTable)
      // the two header copies are maintained by hand and only their last row is compared, so a divergence is not detected here
      var count = Math.min(realCells.length, cloneCells.length)

      for (var i = 0; i < count; i++) {
        var width = realCells[i].getBoundingClientRect().width + 'px'

        cloneCells[i].style.width = width
        cloneCells[i].style.minWidth = width
        cloneCells[i].style.maxWidth = width
      }

      measureColumnEnds(realTable)

      box.style.marginTop = -clone.getBoundingClientRect().height + 'px'
    }

    function lastHeaderRowCells(table) {
      var rows = table.tHead.rows

      return rows.length ? rows[rows.length - 1].cells : []
    }

    function syncClonePosition(box, clone) {
      if (!box || !clone) return

      clone.scrollLeft = box.scrollLeft

      mirrorClass(box, clone, '-scroll-left')
      mirrorClass(box, clone, '-scroll-right')

      updateColumnCue(box)
    }

    function measureColumnEnds(realTable) {
      var tableLeft = realTable.getBoundingClientRect().left

      var labels = realTable.tHead.querySelectorAll(
        '.quota-grid__column-label:not(.-total)'
      )

      columnEnds = [].map.call(labels, function(th) {
        return th.getBoundingClientRect().right - tableLeft
      })

      var totalLabel = realTable.tHead.querySelector(
        '.quota-grid__column-label.-total'
      )

      pinnedRightWidth = totalLabel
        ? totalLabel.getBoundingClientRect().width
        : 0
    }

    function getLegendMoreNode() {
      if (!legendMoreNode || !$element[0].contains(legendMoreNode)) {
        legendMoreNode = $element[0].querySelector('.quota-grid__legend-more')
      }

      return legendMoreNode
    }

    function updateColumnCue(box) {
      var node = getLegendMoreNode()
      if (!node) return

      var hidden = 0

      if (box && columnEnds.length) {
        var visibleRight = box.scrollLeft + box.clientWidth - pinnedRightWidth

        columnEnds.forEach(function(end) {
          if (end > visibleRight + 1) hidden++
        })
      }

      applyColumnCue(node, hidden)
    }

    function applyColumnCue(node, hidden) {
      var legend = node.parentNode
      if (!legend) return

      if (!hidden) {
        node.textContent = ''
        legend.classList.remove('-has-more')
        return
      }

      node.textContent =
        quotaTextService.pluralise(hidden, 'more column', 'more columns') + ' →'

      legend.classList.add('-has-more')
    }

    function mirrorClass(from, to, name) {
      if (from.classList.contains(name)) {
        to.classList.add(name)
      } else {
        to.classList.remove(name)
      }
    }
  }
})()
